// Bring your own numbers.
//
// Someone who already has a model — a spreadsheet, a Python notebook, a tipping
// competition's own ratings — should be able to hand it to the app as a CSV and
// have every strategy, backtest and calibration report treat it exactly like a
// built-in. The only work is the join: their row has a date and two team names,
// and the engine needs a matchKey.
//
// Rows that cannot be joined or carry an impossible probability are returned,
// with the reason, rather than dropped — an upload that silently matched three
// of forty rows is worse than one that says so.

import { SPORTS, isKnownTeam, matchKey as makeMatchKey, teamId } from '../teams.js';
import { assertBefore } from './base.js';
import { zonedIso } from './time.js';

const SELECTIONS = ['home', 'away', 'draw'];

/**
 * One probability, as a fraction.
 *
 * A spreadsheet column of probabilities is as likely to read "62%" as 0.62, so
 * a trailing per-cent sign is taken at its word and divided out. A BARE 62 is
 * not: it could equally be a badly scaled fraction, and guessing which would
 * mean silently betting on a number nobody wrote. It is refused with a reason,
 * which is the whole contract of this module.
 */
function readP(raw) {
  if (typeof raw === 'string' && raw.trim().endsWith('%')) {
    const pct = Number(raw.trim().slice(0, -1).trim());
    return Number.isFinite(pct) ? pct / 100 : NaN;
  }
  return Number(raw);
}

/**
 * User rows -> model state.
 *
 * A row names a match either by `matchKey` outright, or by `date` (the
 * competition's own calendar day) plus `home` and `away`. Noon is used as the
 * instant because it is the middle of the local day in every zone, so no
 * daylight-saving edge can push the fixture onto the day before.
 *
 * `knownTeamsOnly` refuses a team name the sport's alias table has never heard
 * of. Off by default, because `teamId` falls back to a stable slug and two
 * feeds that spell a new club the same way still join; on for anything TYPED
 * by a person, where an unrecognised name is a misspelling that would otherwise
 * produce a row that matches nothing and says nothing.
 */
export function normaliseProbSet(rows, { sport, knownTeamsOnly = false } = {}) {
  const tz = SPORTS[sport]?.tz;
  const out = {};
  const unmatched = [];
  let matched = 0;

  for (const row of rows ?? []) {
    const selection = String(row?.selection ?? '').toLowerCase();
    if (!SELECTIONS.includes(selection)) {
      unmatched.push({ row, reason: `selection must be one of ${SELECTIONS.join(', ')}` });
      continue;
    }
    const p = readP(row?.p);
    if (!Number.isFinite(p) || p <= 0 || p >= 1) {
      unmatched.push({ row, reason: 'probability must be a number in (0, 1)' });
      continue;
    }

    let key = row.matchKey;
    if (!key) {
      if (!tz) {
        unmatched.push({ row, reason: `unknown sport "${sport}"` });
        continue;
      }
      const kickoff = row.date ? zonedIso(`${row.date} 12:00:00`, tz) : null;
      if (!kickoff || !row.home || !row.away) {
        unmatched.push({ row, reason: 'needs a matchKey, or a date plus home and away' });
        continue;
      }
      if (knownTeamsOnly) {
        const unknown = [row.home, row.away].find((name) => !isKnownTeam(sport, name));
        if (unknown) {
          unmatched.push({ row, reason: `no ${sport.toUpperCase()} team called "${unknown}"` });
          continue;
        }
      }
      key = makeMatchKey(sport, kickoff, teamId(sport, row.home), teamId(sport, row.away));
    }

    (out[key] ??= {})[selection] = p;
    matched += 1;
  }

  return { rows: out, matched, unmatched };
}

export const userUpload = {
  id: 'user-upload',
  version: 1,
  sports: null,
  needsFit: false,
  defaultParams: {},

  // Nothing is learned here: the probabilities arrived already formed. `fit`
  // exists so the runner can treat every model the same way.
  fit(history, asOf, params = {}, { strict = false } = {}) {
    assertBefore(history, asOf, strict);
    return { rows: {}, ...params, fittedTo: new Date(asOf).toISOString() };
  },

  predict(state, match, selection) {
    const p = state?.rows?.[match.matchKey]?.[selection];
    return Number.isFinite(p) ? p : null;
  },
};
